import React from "react";
import { useNavigate } from "react-router-dom";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

function ScheduleCard({
  appointments = [],
  selectedDate,
  setSelectedDate,
  scrollRef,
  dates,
}) {
  const navigate = useNavigate();

  const currentMonthYear = selectedDate.toLocaleString("default", {
    month: "long",
    year: "numeric",
  });

  const handleDateClick = (date) => setSelectedDate(date);
  const handleScroll = (dir) =>
    scrollRef.current?.scrollBy({
      left: dir === "left" ? -200 : 200,
      behavior: "smooth",
    });

  const today = new Date().toDateString();
  const schedule = appointments.filter(
    (appt) => new Date(appt.appointmentDate).toDateString() === today
  );
  const upcoming = appointments.filter(
    (appt) => new Date(appt.appointmentDate).toDateString() !== today
  );

  const isEmpty = schedule.length === 0 && upcoming.length === 0;

  const getStatusClasses = (status) => {
    switch (status) {
      case "Confirmed":
        return "bg-green-600 text-white";
      case "Cancelled":
        return "bg-red-600 text-white";
      case "Completed":
        return "bg-yellow-500 text-black";
      default:
        return "bg-gray-500 text-white";
    }
  };

  return (
    <div className="card bg-gray-900">
      {/* Header */}
      <div className="flex justify-between items-center mb-3">
        <h4 className="text-lg font-semibold">{currentMonthYear}</h4>
        <button
          className="bg-green-500 hover:bg-green-600 px-3 py-1 rounded-md text-sm font-medium text-white"
          onClick={() =>
            navigate("/AddAppointment", {
              state: { showModal: true, fromCreateEvent: true },
            })
          }
        >
          + Add New
        </button>
      </div>

      {/* Date Picker */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => handleScroll("left")}
          className="p-2 rounded-full bg-gray-800 hover:bg-gray-700"
        >
          <FiChevronLeft size={18} color="#fff"/>
        </button>
        <div
          ref={scrollRef}
          className="flex gap-2 overflow-x-auto scrollbar-hide flex-1 px-2"
          style={{ scrollBehavior: "smooth" }}
        >
          {dates.map((date, idx) => {
            const dayName = date.toLocaleDateString("en-US", {
              weekday: "short",
            });
            const dayNumber = date.getDate();
            const isSelected =
              selectedDate.toDateString() === date.toDateString();
            return (
              <button
                key={idx}
                onClick={() => handleDateClick(date)}
                className={`flex flex-col items-center px-3 py-2 rounded-lg min-w-[60px] transition-colors ${
                  isSelected
                    ? "bg-green-500 text-white"
                    : "bg-gray-800 text-gray-300"
                }`}
              >
                <span>{dayName}</span>
                <span>{dayNumber}</span>
              </button>
            );
          })}
        </div>
        <button
          onClick={() => handleScroll("right")}
          className="p-2 rounded-full bg-gray-800 hover:bg-gray-700"
        >
          <FiChevronRight size={18} color="#fff"/>
        </button>
      </div>

      {/* Content */}
      {/* Content */}
      {isEmpty ? (
        // ✅ Single empty state if both lists are empty
        <div className="flex flex-col justify-center items-center py-16">
          <p className="text-lg font-medium text-gray-300">
            No appointments found
          </p>
        </div>
      ) : (
        <>
          {/* Schedule (Today) */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <h5 className="font-semibold">Schedule</h5>
              {schedule.length > 0 && (
                <button className="text-xs text-green-400">See All</button>
              )}
            </div>
            <div className="space-y-2">
              {schedule.length > 0 ? (
                schedule.map((item) => (
                  <div
                    key={item.appointmentID}
                    className="flex justify-between items-center bg-gray-800 p-2 rounded-lg"
                  >
                    <div>
                      <p className="text-sm font-medium text-white">{item.patientName}</p>
                      <p className="text-xs text-gray-400">{item.timeSlot}</p>
                      <p className="text-xs text-gray-500">{item.doctorName}</p>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs rounded ${getStatusClasses(
                        item.status
                      )}`}
                    >
                      {item.status}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">
                  No appointments found
                </p>
              )}
            </div>
          </div>

          {/* Upcoming */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <h5 className="font-semibold">Upcoming</h5>
              {upcoming.length > 0 && (
                <button className="text-xs text-green-400">See All</button>
              )}
            </div>
            <div className="space-y-2">
              {upcoming.length > 0 ? (
                upcoming.map((item) => (
                  <div
                    key={item.appointmentID}
                    className="flex justify-between items-center bg-gray-800 p-2 rounded-lg"
                  >
                    <div>
                      <p className="text-sm font-medium text-white">{item.patientName}</p>
                      <p className="text-xs text-gray-400">{item.timeSlot}</p>
                      <p className="text-xs text-gray-500">{item.doctorName}</p>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs rounded ${getStatusClasses(
                        item.status
                      )}`}
                    >
                      {item.status}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">
                  No upcoming appointments
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ScheduleCard;
